import { Link } from "@tanstack/react-router";

/**
 * Coach workspace sub-tab strip — the shared nav across the
 * `/coach` list pages and `/coach/review`.
 *
 * Ports the legacy `setCoachTab` tab strip (`js/coaching.js`) and
 * layers the player-first Board in front of it. The current product
 * collapses object-type library tabs into one Feedback Library tab,
 * and folds follow-up work into Board instead of exposing a second
 * attention surface. Goals is deliberately NOT a tab; goal cards
 * live inside Players.
 *
 * Routing shape (M6a, non-disruptive to M4/M5):
 *   - `/coach` — Phase 1 Squad Feedback Board landing.
 *   - `/coach?tab=roster|library|matches|settings`
 *     — the tabbed shell renders the matching list page. `roster`
 *     remains an explicit deep link during the transition.
 *   - `/coach/review` stays its OWN path (the M4/M5 surface, with
 *     its `match_id`/`note_id`/`clip_id` schema untouched). The
 *     "Review" tab is a link to that path, not a `?tab=review`.
 *
 * Both surfaces render this strip so the workspace feels unified.
 * The active tab is computed from the current location, passed in
 * by each host (the shell knows its `?tab=`; Coach Review passes
 * `"review"`).
 */

export type CoachTab =
  | "board"
  | "roster"
  | "matches"
  | "library"
  | "settings"
  | "review";

/** Tab order + labels. Board leads the strip because bare `/coach`
 * now lands on the Squad Feedback Board. The Players tab keeps the
 * legacy `tab=roster` route value so old deep links keep working. */
const TABS: ReadonlyArray<{ id: CoachTab; label: string }> = [
  { id: "board", label: "Board" },
  { id: "roster", label: "Players" },
  { id: "review", label: "Review" },
  { id: "matches", label: "Matches" },
  { id: "library", label: "Feedback Library" },
  { id: "settings", label: "Settings" },
];

export type CoachTabStripProps = {
  /** The currently-active tab — the host computes it (the shell
   * from its `?tab=`, Coach Review hardcodes `"review"`). */
  active: CoachTab;
};

export function CoachTabStrip({ active }: CoachTabStripProps) {
  return (
    <nav
      className="flex gap-1 overflow-x-auto rounded border border-rule bg-surface p-1 shadow-sm"
      role="tablist"
      aria-label="Coach workspace"
      data-testid="coach-tab-strip"
    >
      {TABS.map((tab) => {
        const isActive = tab.id === active;
        // Review is its own path; the list pages are `/coach?tab=`.
        const linkProps =
          tab.id === "review"
            ? ({ to: "/coach/review" } as const)
            : tab.id === "board"
              ? ({ to: "/coach", search: {} } as const)
            : tab.id === "roster"
              ? ({ to: "/coach", search: { tab: "roster" } } as const)
              : ({ to: "/coach", search: { tab: tab.id } } as const);
        return (
          <Link
            key={tab.id}
            {...linkProps}
            role="tab"
            aria-selected={isActive}
            data-coach-tab={tab.id}
            data-active={isActive}
            className={[
              "shrink-0 rounded px-3 py-1.5 text-sm font-display font-semibold uppercase tracking-wide transition-colors",
              isActive
                ? "bg-accent text-accent-ink"
                : "text-ink-muted hover:bg-field hover:text-ink",
            ].join(" ")}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}
